// src/pages/LearnYourNotesMenu.jsx
// Piano With Pan V1.4 — Learn Your Notes menu (GameMenu-style journey)
//
// Three circles in a single continuous path: CDE → F&G → Note A
// Progressive unlock: B locks until A is done, C locks until B is done.
// Set REACT_APP_LYN_FREE_UNLOCK=true to bypass the lock during testing.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sendGAEvent } from "../analytics/analytics";

/* ───────── completion ───────── */

function isPartDone(id) {
  try { return !!localStorage.getItem(`pwp_lyn_part_${id}_done_v1`); } catch { return false; }
}
const FREE_UNLOCK = (typeof process !== "undefined") &&
  process.env && process.env.REACT_APP_LYN_FREE_UNLOCK === "true";

/* ───────── parts ───────── */

const PARTS = [
  {
    id: "a",
    title: "CDE",
    subtitle: "Learn C, D & E",
    notes: ["C","D","E"],
    accent: {
      bg: "linear-gradient(135deg, #EF4444, #DC2626)",
      ringDark: "#991B1B",
      glow: "rgba(239,68,68,0.32)",
    },
  },
  {
    id: "b",
    title: "F & G",
    subtitle: "Now F and G",
    notes: ["F","G"],
    accent: {
      bg: "linear-gradient(135deg, #3B82F6, #2563EB)",
      ringDark: "#1E40AF",
      glow: "rgba(59,130,246,0.32)",
    },
  },
  {
    id: "c",
    title: "Note A",
    subtitle: "A + Twinkle Twinkle",
    notes: ["A"],
    accent: {
      bg: "linear-gradient(135deg, #EC4899, #DB2777)",
      ringDark: "#9D174D",
      glow: "rgba(236,72,153,0.32)",
    },
  },
];

/* ───────── node ───────── */

function PartNode({ part, status, onClick }) {
  // status: "done" | "active" | "locked"
  const isLocked = status === "locked";
  const isDone   = status === "done";
  const isActive = status === "active";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLocked}
      className={`lyn-node ${isLocked ? "lyn-node-locked" : ""}`}
      aria-label={`${part.title}: ${part.subtitle}${isLocked ? " (locked)" : ""}`}
    >
      <div
        className={`lyn-circle ${isDone ? "lyn-circle-done" : isActive ? "lyn-circle-active" : "lyn-circle-locked"}`}
        style={!isLocked ? {
          background: part.accent.bg,
          borderColor: part.accent.ringDark,
          boxShadow: `0 6px 0 ${part.accent.ringDark}, 0 10px 22px ${part.accent.glow}`,
        } : undefined}
      >
        {isLocked ? (
          <span className="lyn-circle-icon">🔒</span>
        ) : isDone ? (
          <span className="lyn-circle-icon">✓</span>
        ) : (
          <span className="lyn-circle-title">{part.title}</span>
        )}
        {isActive && <span className="lyn-circle-pulse" aria-hidden="true" />}
      </div>
      <div className="lyn-node-label">
        <div className="lyn-node-title">{part.title}</div>
        <div className="lyn-node-sub">{part.subtitle}</div>
      </div>
    </button>
  );
}

/* ───────── main ───────── */

export default function LearnYourNotesMenu() {
  const nav = useNavigate();

  // Refresh on focus so completing a part and returning re-locks/unlocks correctly
  const [doneTick, setDoneTick] = useState(0);
  useEffect(() => {
    const bump = () => setDoneTick(t => t + 1);
    window.addEventListener("focus", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener("focus", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const statuses = useMemo(() => {
    const doneA = isPartDone("a");
    const doneB = isPartDone("b");
    const doneC = isPartDone("c");

    const map = { a: "active", b: "locked", c: "locked" };
    if (doneA) map.a = "done";
    map.b = doneA || FREE_UNLOCK ? (doneB ? "done" : "active") : "locked";
    map.c = (doneA && doneB) || FREE_UNLOCK ? (doneC ? "done" : "active") : "locked";

    // If A is done but B isn't started yet, B should be the "next" active.
    // If A and B done, C is active. Above handles that already.
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneTick]);

  const completedCount = ["a","b","c"].filter(id => statuses[id] === "done").length;
  const allDone = completedCount === 3;

  useEffect(() => {
    sendGAEvent("learn_notes_menu_view", {
      completed_count: completedCount,
      part_a_done: statuses.a === "done",
      part_b_done: statuses.b === "done",
      part_c_done: statuses.c === "done",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePartClick = useCallback((part) => {
    const st = statuses[part.id];
    if (st === "locked") {
      sendGAEvent("learn_notes_menu_locked_click", { part_id: part.id });
      return;
    }
    sendGAEvent("learn_notes_menu_part_click", { part_id: part.id, status: st });
    nav(`/learn-your-notes/${part.id}`);
  }, [statuses, nav]);

  return (
    <div className="lyn-root">
      <style>{CSS}</style>

      {/* Background blobs */}
      <div className="lyn-bg">
        <div className="lyn-bg-base" />
        <div className="lyn-blob lyn-blob-1" />
        <div className="lyn-blob lyn-blob-2" />
      </div>

      {/* Header */}
      <header className="lyn-header">
        <button
          type="button"
          className="lyn-hdr-btn"
          onClick={() => { sendGAEvent("learn_notes_menu_back"); nav("/game-menu"); }}
          aria-label="Back to game menu"
        >
          ←
        </button>
        <div className="lyn-hdr-title">
          <span className="lyn-hdr-emoji">📖</span>
          <span>Learn Your Notes</span>
        </div>
        <div className="lyn-hdr-progress">
          {completedCount}/3
        </div>
      </header>

      {/* Main */}
      <main className="lyn-main">
        <div className="lyn-intro">
          {allDone ? (
            <>
              <div className="lyn-intro-title">🎉 All learned!</div>
              <div className="lyn-intro-sub">You know C, D, E, F, G and A. Time to play some songs.</div>
            </>
          ) : (
            <>
              <div className="lyn-intro-title">Pick a part to begin</div>
              <div className="lyn-intro-sub">Three short steps. Each one teaches you a few new notes.</div>
            </>
          )}
        </div>

        {/* Journey path */}
        <div className="lyn-path">
          {PARTS.map((part, i) => (
            <React.Fragment key={part.id}>
              {i > 0 && (
                <div
                  className={`lyn-connector ${
                    statuses[PARTS[i-1].id] === "done" ? "lyn-connector-done" :
                    statuses[part.id] === "locked"      ? "lyn-connector-locked" :
                                                          "lyn-connector-pending"
                  }`}
                  aria-hidden="true"
                />
              )}
              <PartNode
                part={part}
                status={statuses[part.id]}
                onClick={() => handlePartClick(part)}
              />
            </React.Fragment>
          ))}
        </div>

        {/* After-all-done CTAs */}
        {allDone && (
          <div className="lyn-after-cta">
            <button
              type="button"
              className="lyn-cta-primary"
              onClick={() => { sendGAEvent("learn_notes_menu_to_level1"); nav("/game-menu"); }}
            >
              🐉 Go to Level 1
            </button>
            <button
              type="button"
              className="lyn-cta-secondary"
              onClick={() => { sendGAEvent("learn_notes_menu_to_practice"); nav("/practice"); }}
            >
              🎹 Practice Mode
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.lyn-root {
  min-height: 100dvh;
  font-family: 'Nunito', sans-serif;
  color: #0f172a;
  position: relative;
  overflow-x: hidden;
}

/* ── Background ── */
.lyn-bg { pointer-events: none; position: fixed; inset: 0; z-index: 0; overflow: hidden; }
.lyn-bg-base {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 60%, #f5f3ff 100%);
}
.lyn-blob {
  position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.3;
  animation: lynFloat 20s infinite ease-in-out;
}
.lyn-blob-1 { top: -5%; left: -8%; width: 400px; height: 400px; background: linear-gradient(135deg, #fde68a, #fbbf24); }
.lyn-blob-2 { bottom: -10%; right: -5%; width: 360px; height: 360px; background: linear-gradient(135deg, #c4b5fd, #8b5cf6); animation-delay: 4s; }
@keyframes lynFloat { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(15px,-20px) scale(1.04); } }

/* ── Header ── */
.lyn-header {
  position: sticky; top: 0; z-index: 100;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(14px);
  border-bottom: 1px solid #f1f5f9;
}
.lyn-hdr-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: 2px solid #e2e8f0; background: #fff;
  font-size: 1rem; font-weight: 800; color: #0f172a;
  cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center;
}
.lyn-hdr-btn:hover { border-color: #0f172a; transform: translateY(-1px); }
.lyn-hdr-title {
  display: flex; align-items: center; gap: 8px;
  font-family: 'Fredoka One', cursive; font-size: 1rem; color: #0f172a;
}
.lyn-hdr-emoji { font-size: 1.1rem; }
.lyn-hdr-progress {
  font-family: 'Fredoka One', cursive; font-size: 0.95rem; color: #7c3aed;
  background: #f5f3ff; border: 1.5px solid #ddd6fe;
  padding: 4px 12px; border-radius: 100px;
}

/* ── Main ── */
.lyn-main {
  position: relative; z-index: 1;
  padding: 24px 16px 80px;
  max-width: 460px; margin: 0 auto;
  display: flex; flex-direction: column; align-items: stretch; gap: 18px;
}

.lyn-intro { text-align: center; margin-top: 6px; }
.lyn-intro-title {
  font-family: 'Fredoka One', cursive; font-size: 1.4rem; color: #0f172a;
  margin-bottom: 4px;
}
.lyn-intro-sub {
  font-size: 0.85rem; font-weight: 700; color: #64748b;
  max-width: 320px; margin: 0 auto;
}

/* ── Journey path ── */
.lyn-path {
  display: flex; flex-direction: column; align-items: center; gap: 0;
  padding: 12px 0;
}

.lyn-connector {
  width: 4px; height: 32px; border-radius: 2px;
  background: repeating-linear-gradient(to bottom, #cbd5e1 0px, #cbd5e1 6px, transparent 6px, transparent 12px);
}
.lyn-connector-done {
  background: repeating-linear-gradient(to bottom, #22c55e 0px, #22c55e 6px, transparent 6px, transparent 12px);
}
.lyn-connector-pending {
  background: repeating-linear-gradient(to bottom, #86efac 0px, #86efac 6px, transparent 6px, transparent 12px);
  opacity: 0.7;
}
.lyn-connector-locked {
  background: repeating-linear-gradient(to bottom, #e9d5ff 0px, #e9d5ff 6px, transparent 6px, transparent 12px);
  opacity: 0.6;
}

/* ── Node ── */
.lyn-node {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 6px 0; background: transparent; border: none;
  cursor: pointer; transition: transform 0.2s;
}
.lyn-node:hover:not(:disabled) { transform: translateY(-2px); }
.lyn-node:disabled { cursor: default; }

.lyn-circle {
  position: relative;
  width: 116px; height: 116px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Fredoka One', cursive; color: #fff; flex-shrink: 0;
  border: 5px solid;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
}
.lyn-circle-title { font-size: 1.7rem; line-height: 1; letter-spacing: 0.02em; }
.lyn-circle-icon { font-size: 2.4rem; line-height: 1; }

.lyn-circle-done {
  /* colour applied inline; slight dim so it reads as completed */
  opacity: 0.88;
}
.lyn-circle-active {
  /* colour applied inline */
}
.lyn-circle-locked {
  background: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: 0 3px 0 #cbd5e1 !important;
  opacity: 0.75;
}

.lyn-circle-pulse {
  position: absolute; inset: -10px; border-radius: 50%;
  border: 4px solid currentColor;
  pointer-events: none;
  animation: lynPulseRing 2s ease-out infinite;
  opacity: 0.5;
}
@keyframes lynPulseRing {
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.35); opacity: 0; }
}

.lyn-node-label { text-align: center; }
.lyn-node-title {
  font-family: 'Fredoka One', cursive; font-size: 1.1rem; color: #0f172a;
  line-height: 1.15;
}
.lyn-node:disabled .lyn-node-title { color: #94a3b8; }
.lyn-node-sub {
  font-size: 0.78rem; font-weight: 700; color: #64748b; margin-top: 2px;
}
.lyn-node:disabled .lyn-node-sub { color: #cbd5e1; }

/* ── After-all-done CTAs ── */
.lyn-after-cta {
  display: flex; flex-direction: column; gap: 10px; margin-top: 12px;
}
.lyn-cta-primary {
  padding: 14px 24px; border-radius: 100px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: #fff; border: none; cursor: pointer;
  font-family: 'Fredoka One', cursive; font-size: 1rem;
  box-shadow: 0 6px 0 #5b21b6, 0 9px 22px rgba(109,40,217,0.32);
  transition: transform 0.15s, box-shadow 0.15s;
}
.lyn-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 7px 0 #5b21b6, 0 11px 26px rgba(109,40,217,0.40); }
.lyn-cta-primary:active { transform: translateY(2px); box-shadow: 0 3px 0 #5b21b6; }

.lyn-cta-secondary {
  padding: 12px 22px; border-radius: 100px;
  background: #fff; color: #0369a1;
  border: 2px solid #bae6fd; cursor: pointer;
  font-family: 'Fredoka One', cursive; font-size: 0.92rem;
  transition: transform 0.15s, border-color 0.15s;
}
.lyn-cta-secondary:hover { transform: translateY(-1px); border-color: #7dd3fc; }

/* ── Small screens ── */
@media (max-width: 380px) {
  .lyn-circle { width: 100px; height: 100px; border-width: 4px; }
  .lyn-circle-title { font-size: 1.5rem; }
  .lyn-circle-icon { font-size: 2.1rem; }
}
`;